import { useEffect } from 'react';
import { useAppStore } from '../contexts/store';
import { getTenantConfig } from '../api/tenantApi';
import { getCurrentUser } from '../api/authApi';
import { applyTenantColors } from '../utils/colors';
import { extractSubdomain } from '@pp/shared/platform/tenant';
import { setLocaleDefaults } from '@pp/shared/utils/format';
import { isNativeApp } from '@pp/shared/platform/platform';

/** True when this document is one of the /embed/* widgets rendered inside a host page. */
function isEmbedDocument(): boolean {
  return typeof window !== 'undefined' && window.location.pathname.startsWith('/embed/');
}

/**
 * Handle authentication token from URL (from marketing site login)
 * Returns true if token was found and processed
 */
async function handleIncomingAuth(): Promise<boolean> {
  // Skip auth handling on pages that use 'token' param for different purposes
  // - Password reset/forgot pages use token for password reset flow
  // - Email verification uses token for verification flow
  // - Portal pages use token for magic link authentication (separate auth context)
  const path = window.location.pathname;
  if (path.includes('/reset-password') ||
      path.includes('/forgot-password') ||
      path.includes('/verify-email') ||
      path.startsWith('/portal')) {
    return false;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');

  if (token) {
    // Store the token
    const { setToken, setRefreshToken, setUser } = useAppStore.getState();
    setToken(token);

    // Also check for refresh token in localStorage (set by marketing site)
    const refreshToken = localStorage.getItem('refreshToken');
    if (refreshToken) {
      setRefreshToken(refreshToken);
    }

    // Clean up the URL (remove token param for security)
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.delete('token');
    // Keep tenant param if present (will be handled by extractSubdomain)

    // Replace URL without the token parameter
    window.history.replaceState({}, '', newUrl.toString());

    // Fetch user info to populate role and permissions
    try {
      const user = await getCurrentUser();
      setUser(user);
    } catch (error) {
      console.error('Failed to fetch user info:', error);
    }

    return true;
  }

  return false;
}

/**
 * Hook to initialize and manage tenant configuration
 * This should be called once at the app root level
 */
export function useTenant() {
  const {
    tenantConfig,
    tenantLoading,
    tenantError,
    setTenantConfig,
    setTenantLoading,
    setTenantError,
    effectiveTheme,
    initializeTheme,
  } = useAppStore();

  useEffect(() => {
    const initialize = async () => {
      // Handle incoming auth token from marketing site
      const wasAuthenticated = await handleIncomingAuth();

      // Get current state to check for existing auth
      const { token, user, setUser } = useAppStore.getState();

      // If we have a token but no user data, fetch the current user
      // This handles the case where the app reloads with a token in localStorage
      if (token && !user && !wasAuthenticated) {
        try {
          const currentUser = await getCurrentUser();
          setUser(currentUser);
        } catch (error) {
          console.error('Failed to fetch current user:', error);
          // Token might be invalid/expired - the API client will handle logout
        }
      }

      // Re-check user after potential fetch
      const updatedState = useAppStore.getState();
      const isSuperAdmin = updatedState.user?.role === 'SuperAdmin';

      // SuperAdmins don't need tenant config - redirect to superadmin panel
      if (wasAuthenticated && isSuperAdmin) {
        window.location.href = '/superadmin';
        return;
      }

      const loadTenantConfig = async () => {
        // Portal routes get tenant info from customer account, not URL subdomain
        const isPortalRoute = window.location.pathname.startsWith('/portal');
        if (isPortalRoute) {
          setTenantLoading(false);
          return;
        }

        // Public routes that don't require tenant context (new ISP registration, etc.)
        const isPublicRoute = window.location.pathname.startsWith('/register-isp') ||
                              window.location.pathname === '/register/success' ||
                              window.location.pathname === '/register/canceled';
        if (isPublicRoute) {
          setTenantLoading(false);
          return;
        }

        const subdomain = extractSubdomain();

        // If no subdomain is available, redirect to tenant selection or show error
        if (!subdomain) {
          // Native shell (Dispatch on iOS/Android) loads from capacitor://localhost,
          // so there is no subdomain in the URL on first launch. Don't block the
          // app — let LoginPage render so the user can sign in via SSO (which
          // resolves tenant from the verified email) or email lookup. The tenant
          // subdomain is stored after a successful login and picked up on the
          // next render.
          if (isNativeApp()) {
            // Native shell still needs the saved theme applied on cold
            // start — without this call, the store's effectiveTheme stays
            // at the 'light' default and the user's dark-mode choice gets
            // silently lost every restart. The global theme key
            // (`theme_preference`) is fine here; tenant-scoped theming
            // only kicks in once the user logs in and a tenant resolves,
            // and at that point initializeTheme will re-run with the
            // tenantId from the post-login tenant fetch.
            initializeTheme();
            setTenantLoading(false);
            return;
          }
          // Not an error state: with no tenantConfig the app renders
          // TenantNotFoundPage, whose email → organization lookup is the
          // intended entry path for users who land on the bare host. Setting
          // an error message here just put a scary red box above that
          // perfectly good form.
          setTenantLoading(false);
          return;
        }

        try {
          setTenantLoading(true);
          const config = await getTenantConfig();
          setTenantConfig(config);

          // Initialize locale defaults for formatting
          setLocaleDefaults({
            locale: config.locale,
            currency: config.defaultCurrency,
            dateFormat: config.dateFormat,
            timeFormat: config.timeFormat,
          });

          if (isEmbedDocument()) {
            // Embedded widgets (/embed/*) live inside a tenant's own page,
            // usually a light marketing site. Following the visitor's OS
            // dark-mode preference here painted a black widget with
            // invisible headings inside a white card, so embeds always
            // render the light theme and never touch the dashboard's
            // saved theme preference.
            document.documentElement.classList.remove('dark');
            applyTenantColors(config.primaryColor, config.secondaryColor, false);
          } else {
            // Initialize theme with tenant ID
            initializeTheme(config.tenantId);

            // Apply tenant colors
            applyTenantColors(
              config.primaryColor,
              config.secondaryColor,
              effectiveTheme === 'dark'
            );
          }

          // If user was just authenticated from marketing site, redirect to dashboard
          if (wasAuthenticated) {
            window.location.href = '/dashboard';
          }
        } catch (error) {
          console.error('Failed to load tenant config:', error);
          // Check if this is a TENANT_NOT_FOUND error
          const axiosError = error as { response?: { data?: { code?: string } } };
          if (axiosError?.response?.data?.code === 'TENANT_NOT_FOUND') {
            setTenantError('Organization not found. Please check your URL and try again.');
          } else {
            setTenantError('Failed to load tenant configuration');
          }
        }
      };

      await loadTenantConfig();
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update tenant colors when theme changes
  useEffect(() => {
    if (tenantConfig) {
      applyTenantColors(
        tenantConfig.primaryColor,
        tenantConfig.secondaryColor,
        effectiveTheme === 'dark'
      );
    }
  }, [effectiveTheme, tenantConfig]);

  return {
    tenantConfig,
    tenantLoading,
    tenantError,
  };
}
